import React, { useEffect, useRef } from 'react';
import { useAppSelector } from '../../redux/hook';
import {
	initializeMap,
	initializeMapWithAddress,
	updateMap,
	updateMapWithDriver,
	updateMapDirectionFromBackend,
} from './MapClient.services';
import {
	DRIVER,
	ORDER_FLOW_TYPE,
	ORDER_PHASE,
	ORDER_PHASE_CONTACTING,
	SHOP,
} from '../../constants';
import { getDirectionsBetweenCoordinates } from '../../api/delivery';
import { logHighError } from '../../utils/logger';

type MapClientProps = {
	chargeId: string;
};

export default function MapClient({ chargeId }: MapClientProps) {
	const mapCanvasRef = useRef<HTMLDivElement>(null);
	const directionsRef = useRef(null);
	const directionsLoadedRef = useRef(false);
	const refsRef = useRef({
		map: null as any,
		mapCanvas: null as HTMLDivElement | null,
		directionsDisplay: null as any,
		routePolyline: null as any,
		orderMarker: null as any,
		customerMarker: null as any,
		circle1: null as any,
		circle2: null as any,
		orderLocation: null as any,
	});

	const { drivers, shops, orders } = useAppSelector((state) => ({
		drivers: state.driverReducer.drivers,
		shops: state.shopReducer.shops,
		orders: state.orderReducer.orders,
	}));

	useEffect(() => {
		const order = orders[chargeId];
		if (!order) return;

		const {
			shopId,
			address,
			status,
			orderFlowType,
		} = order;
		const { location } = address;
		const shop = shops[shopId];
		const shopAddress = shop?.address;
		if (!shopAddress) return;

		refsRef.current.mapCanvas = mapCanvasRef.current;

		if (
			orderFlowType === ORDER_FLOW_TYPE.REVERSE &&
			status === ORDER_PHASE_CONTACTING
		) {
			const addressForMap = address?.location ?? address;
			initializeMapWithAddress(
				addressForMap,
				refsRef.current
			);
			updateMap(
				location,
				refsRef.current
			);

			return;
		}

		initializeMap(refsRef.current);

		const driverLocation =
			drivers[chargeId]?.driverLocation || null;

		if (!driverLocation) {
			updateMapWithDriver(
				shopAddress.location,
				location,
				SHOP,
				refsRef.current
			);
		} else {
			updateMapWithDriver(
				driverLocation,
				location,
				DRIVER,
				refsRef.current
			);
		}
	}, [chargeId, orders, drivers, shops]);

	useEffect(() => {
		const loadDirections = async () => {
			try {
				const order = orders[chargeId];
				if (!order) return;

				const {
					shopId,
					address,
					status,
					hasReachedDirectionsThreshold,
					companyId,
				} = order;

				if (status !== ORDER_PHASE.DELIVERING) {
					return;
				}

				const { location } = address;
				const shop = shops[shopId];
				const shopAddress = shop?.address;
				if (!shopAddress) return;

				const driverLocation =
					drivers[chargeId]?.driverLocation || null;
				if (!driverLocation) {
					updateMapWithDriver(
						shopAddress.location,
						location,
						SHOP,
						refsRef.current
					);
					return;
				}
				if (!hasReachedDirectionsThreshold) {
					directionsLoadedRef.current = false;

					if (refsRef.current.routePolyline) {
						refsRef.current.routePolyline.setMap(null);
						refsRef.current.routePolyline = null;
					}

					directionsRef.current = null;

					updateMapWithDriver(
						driverLocation,
						location,
						DRIVER,
						refsRef.current
					);
					return;
				}

				const directions =
					await getDirectionsBetweenCoordinates(
						companyId,
						Number(driverLocation.latitude),
						Number(driverLocation.longitude),
						Number(location.latitude),
						Number(location.longitude)
					);

				directionsRef.current = directions.data;
				directionsLoadedRef.current = true;

				updateMapDirectionFromBackend(
					directionsRef.current,
					driverLocation,
					location,
					DRIVER,
					refsRef.current
				);
			} catch (error) {
				logHighError(
					`Error when updating map direction ${error}`
				);
			}
		};

		loadDirections();
	}, [chargeId, orders, drivers]);

	return (
		<div
			ref={mapCanvasRef}
			style={{ height: '35vh' }}
		/>
	);
}